import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Tab,
  Tabs,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";

interface HealthRecord {
  id: number;
  medical_event: string;
  description: string;
  event_code: string;
  comment: string;
  event_type: string;
}

interface EventLibraryItem {
  id_event_library: number;
  event_code: string;
  event_type: string;
  event_description: string;
}

interface HealthRecordsSectionProps {
  memberId: string;
  refreshTrigger: number;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
}

const HealthRecordsSection: React.FC<HealthRecordsSectionProps> = ({
  memberId,
  refreshTrigger,
  setRefreshTrigger,
}) => {
  const [healthRecords, setHealthRecords] = useState<HealthRecord[]>([]);
  const [eventLibrary, setEventLibrary] = useState<EventLibraryItem[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventLibraryItem | null>(
    null
  );
  const [comment, setComment] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchHealthRecords();
    fetchEventLibrary();
  }, [memberId, refreshTrigger]);

  const fetchHealthRecords = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/health-records`
      );
      setHealthRecords(response.data);
    } catch (error) {
      console.error("Error fetching health records:", error);
    }
  };

  const fetchEventLibrary = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/event-library`
      );
      setEventLibrary(response.data);
    } catch (error) {
      console.error("Error fetching event library:", error);
    }
  };

  const handleAddRecord = async () => {
    if (!selectedEvent) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/health-records`,
        {
          medical_event: selectedEvent.event_description,
          description: selectedEvent.event_description,
          event_code: selectedEvent.event_code,
          comment,
        }
      );
      setIsAddModalOpen(false);
      setSelectedEvent(null);
      setComment("");
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error adding health record:", error);
    }
  };

  const handleDeleteRecord = async (recordId: number) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/health-records/${recordId}`
      );
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting health record:", error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const filteredHealthRecords = healthRecords.filter(
    (record) =>
      record.event_type ===
        ["Diagnosis", "Medication", "Treatment"][activeTab] &&
      (record.medical_event.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.event_code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredEventLibrary = eventLibrary.filter(
    (event) =>
      event.event_type === ["Diagnosis", "Medication", "Treatment"][activeTab]
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Health Records</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsAddModalOpen(true)}
        >
          Add Health Record
        </Button>
      </Box>

      {/* <TextField
        fullWidth
        label="Search by name or code"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      /> */}

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Diagnosis" />
        <Tab label="Medication" />
        <Tab label="Treatment" />
      </Tabs>

      <List>
        {filteredHealthRecords.map((record) => (
          <ListItem
            key={record.id}
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => handleDeleteRecord(record.id)}
              >
                <DeleteIcon />
              </IconButton>
            }
          >
            <ListItemText
              primary={`${record.medical_event} (${record.event_code})`}
              secondary={record.comment}
            />
          </ListItem>
        ))}
      </List>

      <Dialog open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <DialogTitle>Add Health Record</DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Diagnosis" />
            <Tab label="Medication" />
            <Tab label="Treatment" />
          </Tabs>
          <Autocomplete
            options={filteredEventLibrary}
            getOptionLabel={(option) =>
              `${option.event_description} (${option.event_code})`
            }
            renderInput={(params) => (
              <TextField {...params} label="Event" fullWidth margin="normal" />
            )}
            value={selectedEvent}
            onChange={(_, newValue) => setSelectedEvent(newValue)}
          />
          <TextField
            label="Comment"
            fullWidth
            margin="normal"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
          <Button onClick={handleAddRecord} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HealthRecordsSection;
